import { useState, useEffect } from "react"; 
import { Calendar } from "@iroomit/react-date-range";

type FilterDatePickerProps = {
  isOpen: boolean,
  onClose: () => void,
  style?: React.CSSProperties,
  onChange: (selection: { startDate: string | undefined, endDate: string | undefined, key: string }) => void,
  minDate?: Date,
  maxDate?: Date,
  variant?: 'default' | 'ashwin',
  initialRange?: {
    startDate: Date,
    endDate: Date,
  },
};

const FilterDatePicker: React.FC<FilterDatePickerProps> = ({
  onClose,
  onChange,
  style = {},
  isOpen = false,
  minDate,
  maxDate,
  variant = 'default',
  initialRange,
}) => {

  // Helper function to format dates as YYYY-MM-DD strings
  const formatDateOnly = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [range, setRange] = useState<{
    key: string;
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    key: 'selection',
    endDate: initialRange?.endDate || new Date(),
    startDate: initialRange?.startDate || new Date(),
  });

  // Preserve state during re-renders by using a ref to track if we're in the middle of selection
  const [isSelecting, setIsSelecting] = useState(false);

  // Reset range when initialRange changes, but only if not in the middle of selection
  useEffect(() => {
    if (!isSelecting) {
      if (initialRange) {
        setRange({
          key: 'selection',
          startDate: initialRange.startDate,
          endDate: initialRange.endDate,
        });
      } else {
        // When initialRange is null/undefined (filters cleared), reset to current date
        setRange({
          key: 'selection',
          startDate: new Date(),
          endDate: new Date(),
        });
      }
    }
  }, [initialRange, isSelecting]);

  const handleClear = () => {
    const clearedRange: {
      key: string;
      startDate: string | undefined;
      endDate: string | undefined;
    } = {
      key: 'selection',
      startDate: undefined,
      endDate: undefined,
    };
    setRange({
      key: 'selection',
      startDate: undefined,
      endDate: undefined,
    });
    // For clear, immediately trigger onChange
    onChange(clearedRange);
  };

  const handleApply = () => {
    // Format dates as YYYY-MM-DD strings using helper function
    const adjustedRange = {
      key: range.key,
      startDate: range.startDate ? formatDateOnly(range.startDate) : undefined,
      endDate: range.endDate ? formatDateOnly(range.endDate) : undefined,
    };
    
    // Only trigger onChange when apply is clicked
    onChange(adjustedRange);
    setIsSelecting(false);
    onClose();
  };

  const handleCancel = () => {
    // Reset to initial range if provided, otherwise keep current selection
    if (initialRange) {
      setRange({
        key: 'selection',
        startDate: initialRange.startDate,
        endDate: initialRange.endDate,
      });
    }
    setIsSelecting(false);
    onClose();
  };

  if(!isOpen) return <></>;

  if (variant === 'ashwin') {
    return (
      <div
        style={style}
        className="date-range-picker-ashwin"
        onMouseLeave={onClose}
      >
        <div className="ashwin-date-picker-container">
          <div className="ashwin-header">
            <h3>Select Date Range</h3>
            <button 
              className="ashwin-clear-btn"
              onClick={handleClear}
              title="Clear selection"
            >
              Clear
            </button>
          </div>
          <div className="ashwin-date-picker">
            <div className="ashwin-dual-date-pickers">
              {/* Left Date Picker - Start Date */}
              <div className="ashwin-single-picker">
                <div className="ashwin-picker-label">Start Date</div>
                <Calendar
                  date={range.startDate}
                  minDate={minDate}
                  maxDate={range.endDate || maxDate}
                  onChange={(date) => {
                    if (date) {
                      setIsSelecting(true);
                      const newRange = {
                        ...range,
                        startDate: date,
                      };
                      setRange(newRange);
                    }
                  }}
                  className="ashwin-calendar-start"
                />
              </div>

              {/* Right Date Picker - End Date */}
              <div className="ashwin-single-picker">
                <div className="ashwin-picker-label">End Date</div>
                <Calendar
                  date={range.endDate}
                  minDate={range.startDate || minDate}
                  maxDate={maxDate}
                  onChange={(date) => {
                    if (date) {
                      setIsSelecting(true);
                      const newRange = {
                        ...range,
                        endDate: date,
                      };
                      setRange(newRange);
                    }
                  }}
                  className="ashwin-calendar-end"
                />
              </div>
            </div>
          </div>
          <div className="ashwin-footer">
            <div className="ashwin-selected-range">
              {range.startDate && range.endDate && (
                <span>
                  {range.startDate.toLocaleDateString()} - {range.endDate.toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="ashwin-actions">
              <button 
                className="ashwin-cancel-btn"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button 
                className="ashwin-apply-btn"
                onClick={handleApply}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default variant - keep existing behavior for backward compatibility
  return (
    <div
      style={{
        ...style,
        zIndex: 5000,
        padding: "10px",
        borderRadius: "5px",
        position: "absolute",
        color: "transparent",
        transform: "translate(-70%, 13%)",
      }}
      className="date-range-picker"
      onMouseLeave={onClose}
    >
            <Calendar
        date={range.startDate}
        minDate={minDate}
        maxDate={maxDate}
        onChange={(date) => {
          if (date) {
            // Format dates as YYYY-MM-DD strings using helper function
            const newRange = {
              startDate: formatDateOnly(date),
              endDate: formatDateOnly(date),
              key: 'selection',
            };
            setRange({
              startDate: date,
              endDate: date,
              key: 'selection',
            });
            onChange(newRange);
          }
        }}
      />
    </div>
  );
}

export default FilterDatePicker;